"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { channelsAPI, handleApiError } from "../../services/api";
import { resolveImageUrl, withPlaceholder } from "../../utils/imageUtils";
import "./ChannelSettings.css";
import ConfirmModal from "./modals/ConfirmModal";

const ChannelSettings = ({ isOpen, onClose, channel, userRole, onChannelUpdated }) => {
  const { t } = useTranslation();
  const { getUserType } = useAuth();

  const isOwner = userRole === "owner";

  // Get user type color
  const currentUserType = getUserType();
  const getUserTypeColor = (userType) => {
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8',
      'VETERINARIAN': '#00D4B3',
      'SERVICE_PROVIDER': '#D65745',
      'SERVICE_PROVIDER': '#D65745',
      'CLINIC_MODERATOR': '#2A8AD2',
    };
    return userTypeConfig[userType] || '#8548A8';
  };
  const userTypeColor = getUserTypeColor(currentUserType);

  const [formData, setFormData] = useState({
    name: channel?.name || "",
    description: channel?.description || "",
    privacySetting: channel?.privacySetting || "public",
    profileImage: null
  });
  const [profileImagePreview, setProfileImagePreview] = useState(channel?.profileImage || null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };


  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, profileImage: "Only JPG, PNG, and WebP images are allowed" }));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, profileImage: "Image size must be less than 5MB" }));
        return;
      }
      setFormData(prev => ({ ...prev, profileImage: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isOwner) {
      alert(
        t(
          "channelSettings.errors.onlyOwner",
          "Only the channel owner can update channel settings."
        )
      );
      return;
    }

    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = t(
        "channelSettings.errors.nameRequired",
        "Channel name is required"
      );
    } else if (formData.name.trim().length < 3) {
      newErrors.name = t(
        "channelSettings.errors.nameMin",
        "Channel name must be at least 3 characters"
      );
    } else if (formData.name.trim().length > 100) {
      newErrors.name = t(
        "channelSettings.errors.nameMax",
        "Channel name must not exceed 100 characters"
      );
    }
    if (!formData.description.trim()) {
      newErrors.description = t(
        "channelSettings.errors.descriptionRequired",
        "Channel description is required"
      );
    } else if (formData.description.trim().length < 10) {
      newErrors.description = t(
        "channelSettings.errors.descriptionMin",
        "Channel description must be at least 10 characters"
      );
    } else if (formData.description.trim().length > 500) {
      newErrors.description = t(
        "channelSettings.errors.descriptionMax",
        "Channel description must not exceed 500 characters"
      );
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const requestData = {
        name: formData.name,
        description: formData.description,
        privacy_setting: formData.privacySetting,
        profileImage: formData.profileImage
      };

      const response = await channelsAPI.updateChannel(channel.id, requestData);

      if (response.data && response.data.data) {
        const responseData = response.data.data;
        const profileImageUrl = resolveImageUrl(
          responseData.profile_image_url,
          responseData.profile_image,
          responseData.profileImageUrl,
          responseData.profileImage,
          responseData.profile_image_path
        ) || profileImagePreview;

        const updatedChannel = {
          ...channel,
          id: responseData.id,
          name: responseData.name,
          description: responseData.description,
          privacySetting: responseData.privacy_setting,
          isPublic: responseData.privacy_setting === 'public',
          profileImage: withPlaceholder(profileImageUrl, channel.profileImage)
        };

        onChannelUpdated(updatedChannel);
        onClose();
      }
    } catch (error) {
      handleApiError(error);
      if (error.response?.data?.errors) {
        const apiErrors = error.response.data.errors;
        setErrors({
          name: apiErrors.name?.[0] || null,
          description: apiErrors.description?.[0] || null,
          profileImage: apiErrors.profile_image?.[0] || null
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteChannel = async () => {
    try {
      await channelsAPI.deleteChannel(channel.id);
      onClose();
      window.location.href = "/social/channels";
    } catch (error) {
      handleApiError(error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="channel-settings-overlay" onClick={onClose}>
      <div className="channel-settings" onClick={(e) => e.stopPropagation()} style={{ '--user-type-color': userTypeColor }}>
        <div className="channel-settings-header">
          <h2 className="channel-settings-title">
            {t("channelSettings.title", "Channel Settings")}
          </h2>
          <button className="channel-settings-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="channel-settings-form">
          {!isOwner && (
            <div className="channel-settings-no-permission">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <p>
                {t(
                  "channelSettings.noPermission",
                  "Only the channel owner can update channel settings. Admins can only moderate members and approve join requests."
                )}
              </p>
            </div>
          )}

          <div className="channel-settings-form-grid">
            <div className="channel-settings-form-column">
              <div className="channel-settings-form-group">
                <label className="channel-settings-form-label">
                  {t(
                    "channelSettings.fields.nameLabel",
                    "Channel Name *"
                  )}
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t(
                    "channelSettings.fields.namePlaceholder",
                    "Enter channel name"
                  )}
                  className={`channel-settings-form-input ${errors.name ? "error" : ""}`}
                  maxLength={100}
                  minLength={3}
                  disabled={!isOwner}
                  required
                />
                {errors.name && <span className="channel-settings-form-error">{errors.name}</span>}
                <span className="channel-settings-form-hint">
                  {t(
                    "channelSettings.fields.nameHint",
                    "3-100 characters"
                  )}
                </span>
              </div>

              <div className="channel-settings-form-group">
                <label className="channel-settings-form-label">
                  {t(
                    "channelSettings.fields.descriptionLabel",
                    "Description *"
                  )}
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder={t(
                    "channelSettings.fields.descriptionPlaceholder",
                    "Describe what this channel is about"
                  )}
                  className={`channel-settings-form-textarea ${errors.description ? "error" : ""}`}
                  rows="6"
                  maxLength={500}
                  minLength={10}
                  disabled={!isOwner}
                  required
                />
                {errors.description && <span className="channel-settings-form-error">{errors.description}</span>}
                <span className="channel-settings-form-hint">
                  {t(
                    "channelSettings.fields.descriptionHint",
                    "10-500 characters"
                  )}
                </span>
              </div>

              <div className="channel-settings-form-group">
                <label className="channel-settings-form-label">
                  {t(
                    "channelSettings.fields.privacyLabel",
                    "Privacy Setting"
                  )}
                </label>
                <div
                  className={`channel-settings-privacy-options ${!isOwner ? "disabled" : ""}`}
                >
                  <label className="channel-settings-privacy-option">
                    <input
                      type="radio"
                      name="privacySetting"
                      value="public"
                      checked={formData.privacySetting === "public"}
                      onChange={handleInputChange}
                      disabled={!isOwner}
                    />
                    <div className="privacy-option-content">
                      <div className="privacy-option-header">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <path d="M12 2a15 15 0 0 0 0 20"></path>
                          <path d="M12 2a15 15 0 0 1 0 20"></path>
                        </svg>
                        <span>
                          {t(
                            "channelSettings.privacy.publicTitle",
                            "Public"
                          )}
                        </span>
                      </div>
                      <p>
                        {t(
                          "channelSettings.privacy.publicDescription",
                          "Anyone can join instantly"
                        )}
                      </p>
                    </div>
                  </label>
                  <label className="channel-settings-privacy-option">
                    <input
                      type="radio"
                      name="privacySetting"
                      value="private"
                      checked={formData.privacySetting === "private"}
                      onChange={handleInputChange}
                      disabled={!isOwner}
                    />
                    <div className="privacy-option-content">
                      <div className="privacy-option-header">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                        </svg>
                        <span>
                          {t(
                            "channelSettings.privacy.privateTitle",
                            "Private"
                          )}
                        </span>
                      </div>
                      <p>
                        {t(
                          "channelSettings.privacy.privateDescription",
                          "Members need approval to join"
                        )}
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className="channel-settings-form-column">
              <div className="channel-settings-form-group">
                <label className="channel-settings-form-label">
                  {t(
                    "channelSettings.fields.profileLabel",
                    "Profile Image"
                  )}
                </label>
                <label
                  className={`channel-settings-image-upload profile dropzone ${!isOwner ? "disabled" : ""}`}
                >
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleProfileImageChange}
                    disabled={!isOwner}
                  />
                  <div className="channel-settings-profile-preview">
                    {profileImagePreview ? (
                      <img
                        src={profileImagePreview}
                        alt={t(
                          "channelSettings.upload.profilePreviewAlt",
                          "Profile preview"
                        )}
                      />
                    ) : (
                      <div className="channel-settings-profile-placeholder">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                          <circle cx="8.5" cy="8.5" r="1.5"></circle>
                          <polyline points="21 15 16 10 5 21"></polyline>
                        </svg>
                        <span>{t("channelSettings.placeholders.profile", "No profile image")}</span>
                      </div>
                    )}
                  </div>
                  {!profileImagePreview && (
                    <span className="channel-settings-drop-hint">
                      {t("channelSettings.upload.profileUpload", { defaultValue: "UPLOAD PROFILE" })}
                    </span>
                  )}
                </label>
                {errors.profileImage && <span className="channel-settings-form-error">{errors.profileImage}</span>}
              </div>
            </div>
          </div>

          {userRole === "owner" && (
            <div className="channel-settings-danger-zone">
              <button
                type="button"
                className="channel-settings-delete-btn"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
                {t(
                  "channelSettings.danger.delete",
                  "Delete Channel"
                )}
              </button>
              <p className="channel-settings-danger-note">
                {t(
                  "channelSettings.danger.note",
                  "Only the channel owner can delete or update channel settings. Admins can only moderate members and approve join requests."
                )}
              </p>
            </div>
          )}

          <div className="channel-settings-actions">
            <button type="button" className="channel-settings-cancel" onClick={onClose}>
              {t("channelSettings.actions.cancel", "Cancel")}
            </button>
            <button type="submit" className="channel-settings-submit" disabled={isSubmitting || !isOwner}>
              {isSubmitting
                ? t("channelSettings.actions.saving", "Saving...")
                : t("channelSettings.actions.save", "Save Changes")}
            </button>
          </div>
        </form>
      </div>

      {showDeleteConfirm && (
        <ConfirmModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteChannel}
          title={t("channelSettings.danger.delete", "Delete Channel")}
          message={t(
            "channelSettings.deleteConfirm",
            "Are you sure you want to delete this community? This action cannot be undone."
          )}
          cancelLabel={t("common.cancel", "Cancel")}
          showWarningIcon={true}
          confirmLabel={t(
            "channelSettings.danger.delete",
            "Delete community"
          )}
        />
      )}
    </div>
  );
};

export default ChannelSettings;

